import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { slideDownUp } from '../../shared/animations';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgClass } from '@angular/common';
import { MenuModule } from 'headlessui-angular';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthService } from 'src/app/auth/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { ImageService } from 'src/app/core/service/image.service';
import { VendorService } from 'src/app/pages/vendor/vendor.service';
import { AlertService } from 'src/app/core/service/alert.service';
import { TenantPermissionService } from 'src/app/core/service/tenant-permission.service';
import { QuotationUnsavedChangesService } from 'src/app/core/service/quotation-unsaved-changes.service';
import { TimezoneNavigationService } from 'src/app/core/service/timezone-navigation.service';

@Component({
    selector: 'sidebar',
    templateUrl: './sidebar.html',
    animations: [
        slideDownUp,
        trigger('toggleAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))]),
        ]),
    ],
    standalone: true,
    imports: [NgClass, RouterLink, NgScrollbarModule, RouterLinkActive, TranslateModule, MenuModule, SharedModule],
})
export class SidebarComponent implements OnInit, OnDestroy {
    active = false;
    store: any;
    activeDropdown: string[] = [];
    parentDropdown: string = '';
    @ViewChild('sidebarScroll') sidebarScroll!: ElementRef;
    @ViewChild('mastersDropdown') mastersDropdown!: ElementRef;
    profile_picture: string = '';
    VendorName: string = '';
    private readonly destroy$ = new Subject<void>();
    vendorData: any = {};
    vendorLoginData: any;

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
        private readonly cdr: ChangeDetectorRef,
        public authService: AuthService,
        private readonly imageService: ImageService,
        private readonly vendorService: VendorService,
        private readonly alertService: AlertService,
        public readonly tenantPermissionService: TenantPermissionService,
        private readonly quotationUnsavedChangesService: QuotationUnsavedChangesService,
        private readonly timezoneNavigationService: TimezoneNavigationService,
    ) {
    }

    ngOnInit() {
        this.tenantPermissionService.loadTenantPermissions().pipe(takeUntil(this.destroy$)).subscribe();
        this.initStore();
        this.setActiveDropdown();
        // Subscribe to route changes - but don't auto-open dropdowns
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                // Route changed - parent menu items will show as active via isPurchaseActive() / isAdminMasterActive()
                // But don't auto-open dropdowns
            }
        });
        // close flyout when clicking outside
        document.addEventListener('click', this.handleDocumentClick, true);
        this.setupSubscriptions();
        this.vendorData = this.authService.getVendorData();
        this.getVendorById();
    }

    initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }
    /**
     * Setup all subscriptions with proper cleanup
     */
    private setupSubscriptions(): void {
        // Profile picture subscription
        this.authService.profilePicture$.pipe(takeUntil(this.destroy$)).subscribe((profilePicture) => {
            this.profile_picture = profilePicture;
            this.cdr.detectChanges();
        });

        // Profile name subscription
        this.authService.profileName$.pipe(takeUntil(this.destroy$)).subscribe((profileName) => {
            this.VendorName = profileName?.company_name || profileName?.name || '';
            this.cdr.detectChanges();
        });
    }

    /**
     * Fetch vendor profile with proper error handling
     */
    private getVendorById(): void {
        this.vendorService
            .fetchProfile()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    const failed = res?.status === false;
                    const data = failed ? null : res?.data ?? res;
                    if (data?.id != null) {
                        this.processVendorData(data);
                    }
                },
                error: (error) => {
                    this.alertService.showAlert('Error', error?.error?.message || 'Failed to process vendor data', 'error');
                },
            });
    }

    /**
     * Process vendor data from API response
     */
    private processVendorData(data: any): void {
        this.vendorLoginData = data;
        this.profile_picture = data?.profile_picture || '';
        this.VendorName = data?.company_name || data?.name || '';

        this.authService.applyVendorProfilePayload(data);
        this.vendorData = { ...(this.authService.getVendorData() || {}) };
    }

    setActiveMenuBasedOnRoute() {
        // Don't auto-open dropdowns on route change
        // Only update activeDropdown if user manually opened it
        // The parent menu items will still show as active via isPurchaseActive() / isAdminMasterActive()
        // This allows the menu item to be highlighted while keeping dropdown closed
    }

    isPurchaseActive(): boolean {
        return this.router.url.startsWith('/purchase-master');
    }

    isAdminMasterActive(): boolean {
        return this.router.url.startsWith('/master');
    }

    setActiveDropdown() {
        const selector = document.querySelector('.sidebar ul a[routerLink="' + window.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                // Previously this auto-clicked the parent nav-link to expand the accordion on load.
                // Removed to prevent auto-opening menus when the page reloads.
            }
        }
    }

    toggleMobileMenu() {
        if (window.innerWidth < 1024) {
            this.storeData.dispatch({ type: 'toggleSidebar' });
        }
    }

    closeDropdownOnSubmenuClick() {
        // Close all accordions immediately when a submenu item is clicked
        // This ensures dropdown closes right away, before navigation happens
        this.activeDropdown = [];

        // Also close any open flyouts (for collapsed sidebar)
        this.closeAllFlyouts();

        // Prevent auto-opening after navigation by clearing dropdowns again after a short delay
        setTimeout(() => {
            this.activeDropdown = [];
            this.closeAllFlyouts();
        }, 100);
    }

    toggleAccordion(name: string, parent?: string) {
        if (this.activeDropdown.includes(name)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
        } else {
            // Close other accordions when opening a new one (only for top-level accordions)
            if (name === 'Purchase' || name === 'AdminMaster') {
                this.activeDropdown = this.activeDropdown.filter((d) => d !== 'Purchase' && d !== 'AdminMaster');
            }
            this.activeDropdown.push(name);
        }
    }

    toggleNestedAccordion(name: string, event?: MouseEvent) {
        // First, ensure parent 'AdminMaster' is open
        const wasParentClosed = !this.activeDropdown.includes('AdminMaster');
        if (wasParentClosed) {
            this.activeDropdown.push('AdminMaster');
        }

        // Then toggle the nested accordion
        const isOpening = !this.activeDropdown.includes(name);

        if (this.activeDropdown.includes(name)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
            // Remove upward shift class from parent when closing nested menu
            // this.removeParentShiftClass();
        } else {
            this.activeDropdown.push(name);
        }

        // Check if we need to expand upward and shift parent (only for default sidebar, not vertical)
        if (isOpening && event && !this.isVerticalSidebar) {
            const delay = wasParentClosed ? 600 : 350;
            setTimeout(() => {
                this.checkNestedDropdownPosition(event.target as HTMLElement);
            }, delay);
        }
    }

    private checkNestedDropdownPosition(element: HTMLElement) {
        // Only run this logic for default sidebar, not vertical sidebar
        if (this.isVerticalSidebar) {
            return;
        }

        if (!this.sidebarScroll) return;

        const liElement = element.closest('li.nested-accordion-wrapper');

        if (!liElement) return;

        // Position checking logic can be added here if needed
    }

    get isCollapsed(): boolean {
        const nav = document.querySelector('nav.sidebar');
        return !!nav?.closest('.collapsed-default');
    }

    get isVerticalSidebar(): boolean {
        const body = document.body;
        return body.classList.contains('vertical') || body.classList.contains('collapsible-vertical');
    }

    onNavClick(name: string, $event: MouseEvent, route?: string) {
        if (route && this.timezoneNavigationService.blockNavigationIfNeeded(route)) {
            $event.preventDefault();
            $event.stopPropagation();
            return;
        }

        const target = $event.target as HTMLElement;
        const sidebarEl = target.closest('nav.sidebar');
        const isCollapsed = !!sidebarEl?.closest('.collapsed-default');

        if (isCollapsed) {
            // Prevent default accordion behavior; open flyout instead
            $event.preventDefault();
            $event.stopPropagation();

            const li = target.closest('li.menu.nav-item');
            if (!li) return;

            // close other flyouts first
            this.closeAllFlyouts(sidebarEl as HTMLElement);

            // toggle this one
            li.classList.toggle('open-flyout');
            return;
        }

        // Check if this is a direct menu item (not an accordion)
        const button = target.closest('button');
        const hasRouterLink = button?.hasAttribute('routerLink');

        // If it's a direct menu item with routerLink, close all accordions
        if (hasRouterLink && name !== 'Purchase' && name !== 'AdminMaster') {
            // Close all accordions when clicking on direct menu items
            this.activeDropdown = [];
            return;
        }

        // Not collapsed: behave as accordion
        // Close other accordions first, then toggle this one
        if (name === 'Purchase' || name === 'AdminMaster') {
            // Prevent default behavior for accordion buttons
            $event.preventDefault();
            $event.stopPropagation();

            // Close other accordions
            if (name === 'Purchase') {
                this.activeDropdown = this.activeDropdown.filter((d) => d !== 'AdminMaster');
            } else if (name === 'AdminMaster') {
                this.activeDropdown = this.activeDropdown.filter((d) => d !== 'Purchase');
            }
            // Toggle this accordion
            this.toggleAccordion(name);
        } else {
            // For non-accordion items, just close all accordions
            this.activeDropdown = [];
        }
    }

    private closeAllFlyouts(scope?: HTMLElement | Document) {
        const root: HTMLElement | Document = scope || document;
        const openItems = root.querySelectorAll('li.menu.nav-item.open-flyout');
        openItems.forEach((el) => el.classList.remove('open-flyout'));
    }

    private readonly handleDocumentClick = (ev: Event) => {
        const t = ev.target as HTMLElement;
        const anyOpen = document.querySelector('li.menu.nav-item.open-flyout');
        if (!anyOpen) return;

        // if click is inside an open item or its flyout, keep it open
        const openLi = anyOpen as HTMLElement;
        if (openLi.contains(t)) return;

        // otherwise close all
        this.closeAllFlyouts();
    };

    async signOut() {
        const canLeave = await this.quotationUnsavedChangesService.confirmLeaveIfNeeded();
        if (!canLeave) {
            return;
        }
        this.authService.logout();
    }

    ngOnDestroy(): void {
        document.removeEventListener('click', this.handleDocumentClick, true);
        this.destroy$.next();
        this.destroy$.complete();
    }
}
